import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'

import { ReactModal, DatePicker, TimePicker } from '../../../components'
import { COLOR, IMG, getFontContrastColor } from '../../../themes'
import {
  CREATE_DEFAULT,
  DESK_FLOOR_PLAN,
  FALLBACK_SPACE_TYPE_THEME,
  FLOORPLAN,
  MODAL_CONFIRM,
  SPACE_TYPE,
  getLocaleDateString,
  hexToRgba,
} from '../../../helpers'
import DeskOverlay from './DeskOverlay'
import DeskSearch, { findMatchedDesks, getDeskSearchLabel } from './DeskSearch'
import { fetchDesks } from './service'
import Style from './style'

const toMinutes = (time) => {
  const [hour, minute] = String(time || '').split(':').map(Number)
  return (hour || 0) * 60 + (minute || 0)
}

const formatMinutes = (minutes) => [Math.floor(minutes / 60), minutes % 60]
  .map((value) => String(value).padStart(2, '0'))
  .join(':')

const getTimeRange = (date, minuteStep, startOperatingTime, endOperatingTime) => {
  const step = minuteStep > 0 ? minuteStep : 15
  let first = toMinutes(startOperatingTime || '00:00')
  const last = (endOperatingTime ? toMinutes(endOperatingTime) : 24 * 60) - step

  if (moment(date).isSame(new Date(), 'day')) {
    const now = new Date()
    first = Math.max(first, Math.floor((now.getHours() * 60 + now.getMinutes()) / step) * step)
  }

  return { first, last }
}

const MODAL_SHADOW = '0 8px 10px -6px rgba(0, 0, 0, 0.1), 0 20px 25px -5px rgba(0, 0, 0, 0.1)'

const atMinutes = (date, minutes) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, minutes)

const getPalette = (colorTheme, themeConfig) => {
  const main = colorTheme || COLOR.GOLD_1
  const deskTheme = (themeConfig && themeConfig.spaceType && themeConfig.spaceType[SPACE_TYPE.DESK]) ||
    FALLBACK_SPACE_TYPE_THEME[SPACE_TYPE.DESK]

  return {
    main,
    onMain: getFontContrastColor(main),
    tint: hexToRgba(main, 0.1),
    deskBg: deskTheme.background || deskTheme.bg,
    deskFg: deskTheme.foreground || deskTheme.text,
  }
}

const toDay = (date) => {
  const value = date ? new Date(date) : new Date()
  return new Date(value.getFullYear(), value.getMonth(), value.getDate())
}

const DeskFloorPlanModal = ({
  isVisible,
  language,
  colorTheme,
  themeConfig,
  floorId,
  floorPlan,
  initialDate,
  initialStartTime,
  selectedDeskId,
  currentDeskId,
  minuteStep,
  startOperatingTime,
  endOperatingTime,
  getHolidays,
  listHoliday,
  onClose,
  onConfirm,
}) => {
  const getRange = (forDate) => getTimeRange(forDate, minuteStep, startOperatingTime, endOperatingTime)

  const getInitialTime = () => {
    if (initialStartTime) return moment(initialStartTime).format('HH:mm')
    const { first, last } = getRange(toDay(initialDate))
    return first <= last ? formatMinutes(first) : null
  }

  const [date, setDate] = useState(() => toDay(initialDate))
  const [time, setTime] = useState(getInitialTime)
  const [desks, setDesks] = useState([])
  const [status, setStatus] = useState('loading')
  const [selectedId, setSelectedId] = useState(() => selectedDeskId || null)
  const [isShowDatePicker, setIsShowDatePicker] = useState(false)
  const [isShowTimePicker, setIsShowTimePicker] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [search, setSearch] = useState(null)
  const requestRef = useRef(0)
  const firstMatchRef = useRef(null)

  const timeRange = getRange(date)

  useEffect(() => {
    if (!isVisible) return
    setDate(toDay(initialDate))
    setTime(getInitialTime())
    setSelectedId(selectedDeskId || null)
    setIsShowDatePicker(false)
    setIsShowTimePicker(false)
    setIsSearchOpen(false)
    setSearch(null)
  }, [isVisible])

  useEffect(() => {
    if (!isVisible || !floorId || !time) return

    const requestId = ++requestRef.current
    const [hour, minute] = time.split(':').map(Number)
    const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute)

    setStatus('loading')
    fetchDesks(floorId, dateTime)
      .then((list) => {
        if (requestId !== requestRef.current) return
        setDesks(list)
        setStatus('ready')
      })
      .catch(() => {
        if (requestId !== requestRef.current) return
        setDesks([])
        setStatus('error')
      })
  }, [isVisible, floorId, date.valueOf(), time])

  const isDeskBusy = (desk) => desk.isUsing && String(desk.id) !== String(currentDeskId)
  const selectedDesk = desks.find((desk) => String(desk.id) === String(selectedId))
  const isSelectedBusy = selectedDesk ? isDeskBusy(selectedDesk) : false
  const matchedDesks = status === 'ready' ? findMatchedDesks(desks, search) : []

  useEffect(() => {
    if (firstMatchRef.current) firstMatchRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [search, status])

  if (!isVisible || !floorPlan) return null

  const ownerName = isSelectedBusy ? selectedDesk.ownerName : null
  const canConfirm = status === 'ready' && Boolean(time) && Boolean(selectedDesk) && !isSelectedBusy

  const handleSelectDate = (selectedDate) => {
    const nextDate = toDay(selectedDate)
    const { first, last } = getRange(nextDate)
    setDate(nextDate)
    setIsShowDatePicker(false)
    if (time && toMinutes(time) < first) {
      setTime(first <= last ? formatMinutes(first) : null)
    }
  }

  const handleSelectTime = (selectedTime) => {
    setTime(moment(selectedTime).format('HH:mm'))
    setIsShowTimePicker(false)
  }

  const handleConfirm = () => {
    if (!canConfirm) return
    const [hour, minute] = time.split(':').map(Number)
    onConfirm({
      desk: selectedDesk,
      date,
      startTime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute),
    })
  }

  const handleCloseSearch = () => {
    setIsSearchOpen(false)
    setSearch(null)
  }

  const renderPlanMessage = () => {
    if (status === 'loading') return DESK_FLOOR_PLAN.LOADING[language]
    if (status === 'error') return DESK_FLOOR_PLAN.LOAD_FAILED[language]
    if (desks.length === 0) return DESK_FLOOR_PLAN.NO_DESK[language]
    if (search && matchedDesks.length === 0) return DESK_FLOOR_PLAN.SEARCH_NO_MATCH[language]
    return null
  }

  const planMessage = renderPlanMessage()
  const title = [floorPlan.buildingName, floorPlan.floorName || floorPlan.floorLevel].filter(Boolean).join(', ')

  return (
    <ReactModal
      isVisible={isVisible}
      width={1000}
      customStyle={{ content: { maxWidth: '96vw', borderRadius: 0, overflow: 'hidden', boxShadow: MODAL_SHADOW } }}
      onRequestClose={onClose}
    >
      <Style palette={getPalette(colorTheme, themeConfig)}>
        <div className='desk-modal-heading'>
          <div className={`desk-modal-title-row${isSearchOpen ? ' is-searching' : ''}`}>
            <label className='desk-modal-title' title={title}>{title}</label>
            {isSearchOpen ? (
              <DeskSearch language={language} onSearch={setSearch} onClose={handleCloseSearch} />
            ) : (
              <button
                type='button'
                className='desk-modal-icon-button'
                aria-label='open search'
                onClick={() => setIsSearchOpen(true)}
              >
                <img src={IMG['ic_lucide_search.svg']} alt='' />
              </button>
            )}
          </div>

          <div className='desk-modal-legend'>
            <span className='legend-item available'>{FLOORPLAN.AVAILABLE[language]}</span>
            <span className='legend-item busy'>{FLOORPLAN.BUSY[language]}</span>
          </div>

          <div className='desk-modal-filters'>
            <button type='button' className='filter-input date-input' onClick={() => setIsShowDatePicker(true)}>
              <span>{getLocaleDateString(date, language)}</span>
              <img src={IMG['ic_calendar.svg']} alt='calendar' />
            </button>
            <button
              type='button'
              className='filter-input time-input'
              disabled={timeRange.first > timeRange.last}
              onClick={() => setIsShowTimePicker(true)}
            >
              <span>{time || '-'}</span>
              <img src={IMG['chevron-down.svg']} alt='' />
            </button>
          </div>
        </div>

        <div className='desk-modal-body'>
          <div className={`desk-modal-plan${selectedDesk && status === 'ready' ? ' has-card' : ''}`}>
            <div className='plan-viewport'>
              <div className={`plan-canvas${status === 'loading' ? ' is-loading' : ''}`}>
                <img
                  className='plan-image'
                  src={`data:image/png;base64, ${floorPlan.imageBase64}`}
                  alt='Floor layout.'
                />
                <DeskOverlay
                  desks={desks}
                  imageWidth={floorPlan.imageWidth}
                  imageHeight={floorPlan.imageHeight}
                  selectedId={selectedDesk ? selectedDesk.id : null}
                  matchedIds={matchedDesks.map((desk) => desk.id)}
                  isDeskBusy={isDeskBusy}
                  onSelect={(desk) => setSelectedId(desk.id)}
                />
                {matchedDesks.map((desk, index) => (
                  <div
                    key={desk.id}
                    ref={index === 0 ? firstMatchRef : null}
                    className='desk-tooltip'
                    style={{
                      left: `${(desk.box.x / floorPlan.imageWidth) * 100}%`,
                      top: `${((desk.box.y - desk.box.height / 2) / floorPlan.imageHeight) * 100}%`,
                    }}
                  >
                    <span>{getDeskSearchLabel(desk, search.type)}</span>
                  </div>
                ))}
              </div>
            </div>

            {planMessage && <div className='plan-message'>{planMessage}</div>}

            {selectedDesk && status === 'ready' && (
              <div className='desk-card'>
                <div className='desk-card-divider' />
                <div className='desk-card-content'>
                  <div className='desk-card-name'>{selectedDesk.name}</div>
                  {ownerName && <div className='desk-card-name'>{ownerName}</div>}
                  <div className='desk-card-meta'>
                    <span className='meta-item'>
                      <img src={IMG['location.svg']} alt='' />
                      {`${DESK_FLOOR_PLAN.FLOOR[language]} ${floorPlan.floorLevel}`}
                    </span>
                    {selectedDesk.capacity != null && (
                      <span className='meta-item'>
                        <img src={IMG['ic_person.svg']} alt='' />
                        {`${selectedDesk.capacity} ${DESK_FLOOR_PLAN.PERSONS[language]}`}
                      </span>
                    )}
                    <span className='meta-item'>
                      <img src={IMG['ic_desk.svg']} alt='' />
                      {DESK_FLOOR_PLAN.DESK[language]}
                    </span>
                  </div>
                  {selectedDesk.facilities.length > 0 && (
                    <div className='desk-card-chips'>
                      {selectedDesk.facilities.map((facility) => (
                        <span key={facility} className='chip'>{facility}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='desk-modal-footer'>
          <button type='button' className='btn-cancel' onClick={onClose}>
            {MODAL_CONFIRM.KEEP[language]}
          </button>
          <button type='button' className='btn-confirm' disabled={!canConfirm} onClick={handleConfirm}>
            {CREATE_DEFAULT.CONFIRM[language]}
          </button>
        </div>

        {isShowDatePicker && (
          <DatePicker
            selectDate={date}
            handleCloseButton={() => setIsShowDatePicker(false)}
            handleSaveButton={(event, selectedDate) => handleSelectDate(selectedDate)}
            startSelectable={new Date()}
            language={language}
            getHolidays={getHolidays}
            listHoliday={listHoliday}
            colorTheme={colorTheme}
          />
        )}

        {isShowTimePicker && (
          <TimePicker
            selectTime={time ? atMinutes(date, toMinutes(time)) : null}
            selectedDate={date}
            handleCloseButton={() => setIsShowTimePicker(false)}
            handleSaveButton={(event, selectedTime) => handleSelectTime(selectedTime)}
            startSelectable={atMinutes(date, timeRange.first)}
            endSelectable={atMinutes(date, timeRange.last)}
            minuteStep={minuteStep}
            language={language}
            colorTheme={colorTheme}
          />
        )}
      </Style>
    </ReactModal>
  )
}

export default DeskFloorPlanModal
